import axiosFactory, { METHOD_GET, METHOD_POST } from "./AxiosFactory";
import {
  BILLING_ME,
  BILLING_TRIAL,
  BILLING_CHECKOUT,
  BILLING_CONFIRM,
  BILLING_CANCEL,
  BILLING_RESUME,
  BILLING_SANDBOX_COMPLETE,
  LAWYER_RECORD_VIEW,
  LAWYER_RECORD_CALL,
  LAWYER_MY_STATS,
} from "./UrlConstants";

// AvoConsulte Pro, client side. The server decides everything (price, trial,
// dates); this file only calls it and formats what it returns.

const LOCALES = { ar: "ar-TN-u-nu-latn", fr: "fr-FR", en: "en-GB" };
const localeOf = (lang) => LOCALES[lang] || LOCALES.fr;

// { ok, data } or { ok: false, status, code, message }. AxiosFactory puts the
// server's message in `data` on errors, and a network message string on
// failures. Show errors with errorText (./apiErrors), never `message`.
async function call(url, method, data) {
  const res = await axiosFactory({ url, method, data });
  if (res.status === 200 && res.data && typeof res.data === "object") {
    return { ok: true, data: res.data.data };
  }
  return { ok: false, status: res.status, code: res.code || null, message: typeof res.data === "string" ? res.data : "" };
}

export const billingApi = {
  me: () => call(BILLING_ME, METHOD_GET),
  trial: () => call(BILLING_TRIAL, METHOD_POST, {}),
  checkout: (purpose = "subscribe") => call(BILLING_CHECKOUT, METHOD_POST, { purpose }),
  confirm: (session) => call(BILLING_CONFIRM, METHOD_POST, { session }),
  cancel: () => call(BILLING_CANCEL, METHOD_POST, {}),
  resume: () => call(BILLING_RESUME, METHOD_POST, {}),
  sandboxComplete: (body) => call(BILLING_SANDBOX_COMPLETE, METHOD_POST, body),
  myStats: () => call(LAWYER_MY_STATS, METHOD_GET),
};

// Public profile audience; never blocks or reports anything to the visitor.
export function recordProfileEvent(kind, lawyerId) {
  if (!lawyerId) return;
  const url = kind === "call" ? LAWYER_RECORD_CALL : LAWYER_RECORD_VIEW;
  axiosFactory({ url, method: METHOD_POST, data: { lawyerId } }).catch(() => {});
}

// 49000 millimes -> "49" (or "49,500").
export function formatAmount(millimes, lang) {
  return new Intl.NumberFormat(localeOf(lang), { maximumFractionDigits: 3 }).format((millimes || 0) / 1000);
}

export function formatDay(date, lang) {
  if (!date) return "";
  return new Intl.DateTimeFormat(localeOf(lang), {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Africa/Tunis",
  }).format(new Date(date));
}

// Whole days left until `date` (0 on the day itself).
export function daysUntil(date, now = new Date()) {
  if (!date) return 0;
  return Math.max(0, Math.ceil((new Date(date).getTime() - now.getTime()) / 86400000));
}

// The day a trial started now would end.
export function trialEndIfStartedNow(trialDays, now = new Date()) {
  return new Date(now.getTime() + trialDays * 86400000);
}

// One word for the dashboard and banners.
export function billingState(summary) {
  if (!summary || summary.status === "none" || summary.status === "expired") return "free";
  if (summary.status === "comped") return "comped";
  if (summary.status === "past_due") return "past_due";
  if (summary.cancelAtPeriodEnd) return "canceling";
  return summary.status; // trialing | active
}
